"use client";

import { useLayoutEffect, useRef, useState } from "react";
import { Emoji } from "@/components/ui/Emoji";
import { useAnchoredPosition } from "@/lib/hooks/useAnchoredPosition";
import { VIEWPORT_MARGIN, visualViewportRect } from "@/lib/anchoredPosition";

/**
 * The overlapping reaction pills and their total, shared by a post's summary
 * and a DM's.
 *
 * The two drew this identically — same `-space-x-0.5` stack, same 20px circles,
 * same `zIndex` descent, same count span — and only differed in what wrapped
 * it: the post version is a hover target, the DM version a button you can also
 * tap. That difference stays at the call site, where it belongs.
 */
export function ReactionPillStack({
  entries,
  total,
}: {
  entries: Array<{ emoji: string; url?: string | null }>;
  total: number;
}) {
  return (
    <>
      <span className="flex items-center -space-x-0.5">
        {entries.map((entry, i) => (
          <span
            key={entry.emoji}
            /**
             * THE CHIP HAS TO BE VISIBLE ON ITS OWN (OSO-554).
             *
             * It was `bg-surface`, which is the colour of the card and the DM
             * bubble it sits on — so the chip had no shape, and an emoji drawn
             * in light pixels on a transparent ground had nothing behind it.
             * `bg-muted` is a shade off the surface in both themes and
             * `border-border` gives the circle an edge, so a chip is countable
             * even when its glyph is nearly invisible.
             *
             * `text-foreground` is the other half: a pictograph with no emoji
             * presentation (✳, ❤, ✔ without VS16) is drawn as TEXT in
             * currentColor, and the meta row this stack lives in is muted grey.
             * The glyph inherited the timestamp's colour. Tokens throughout —
             * no `dark:` pairs to add to the ratchet.
             */
            className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-border bg-muted text-foreground text-sm leading-none"
            style={{ zIndex: entries.length - i }}
          >
            <Emoji emoji={entry.emoji} url={entry.url} size="sm" />
          </span>
        ))}
      </span>
      <span className="text-sm text-muted-foreground">{total}</span>
    </>
  );
}

/** As wide as the card may get before a long list of names wraps. */
const CARD_MAX_WIDTH = 280;

/**
 * THE CAP THE CLAMP WAS COMPUTED FOR, AND THE ONE THE CARD IS DRAWN AT — the
 * same number, which is the whole of OSO-572.
 *
 * `computeAnchoredPosition` places `left` so that a box `pos.width` wide fits
 * between the margins, and `pos.width` already yields to the viewport
 * (`min(measured, viewport − 2·margin)`). The card, though, was drawn at a flat
 * `CARD_MAX_WIDTH` of its own. On any screen narrower than 296px the two
 * disagreed: the left edge was pinned at the margin and the extra hung off the
 * RIGHT, because clamping one edge cannot hold both when the box is wider than
 * the space there is.
 *
 * Read at render rather than passed in, because the MEASURING pass needs it
 * too. Measuring a card capped at 280 and then drawing it at 264 would make the
 * measured height a lie about the box on screen — the card re-wraps taller and
 * the height cap it was given no longer covers it. Same cap on both passes, so
 * `pos.width` comes back equal to what was measured and nothing re-lays out.
 *
 * There is no SSR hazard in the `window` read: this card only ever mounts on a
 * hover or a tap.
 */
function cardMaxWidth(): number {
  return Math.min(CARD_MAX_WIDTH, visualViewportRect().width - VIEWPORT_MARGIN * 2);
}

/**
 * The shortest box worth scrolling inside — two lines plus the padding.
 *
 * Taken as `Math.min` with the card's own height, never on its own: a
 * two-line tooltip is shorter than this, and a floor that outran the content
 * is precisely what `minHeight` was added to `computeAnchoredPosition` to
 * escape. Below it the final clamp keeps the box on screen instead.
 */
const MIN_CARD_HEIGHT = 64;

/**
 * "Who reacted", the card itself.
 *
 * ONE CHROME, and it is the DM's. That copy used `bg-foreground` /
 * `text-background` — `Tooltip.tsx`'s own pairing — with a comment saying it
 * chose them to get the always-inverted chip look in both themes *without*
 * growing the hand-written `dark:` count. The post copy had
 * `bg-gray-800 dark:bg-gray-900`, which is exactly the pair that comment was
 * avoiding. Taking the better of the two deletes a ratchet entry.
 *
 * `pointer-events-none` is load-bearing on both surfaces: the card is
 * positioned over the content above it, and without this it swallows clicks on
 * that content's links and images — and, on touch, the taps that are supposed
 * to dismiss it.
 *
 * ==========================================================================
 * IT IS `position: fixed`, AND THAT IS THE BUG FIX (OSO-554)
 * ==========================================================================
 *
 * It used to be `absolute bottom-full left-1/2 -translate-x-1/2`. In the DM
 * window that put it inside `ConversationThread`'s `flex-1 overflow-y-auto`
 * scroller, which clips it — and `overflow-y: auto` forces `overflow-x` to
 * `auto` too, so a card wider than the 30rem window lost its left edge as well
 * as its top. A `fixed` box is not clipped by an ancestor's overflow, so no
 * portal is needed here (`ProfileHoverCard` needs one because it stays
 * `absolute` in page coordinates); keeping it inline is also what keeps
 * `DmReactionSummary`'s outside-click dismissal working, since the card is
 * still inside the ref'd container.
 *
 * `fixed` costs the CSS centring, which is why the card measures itself and
 * asks `useAnchoredPosition` where to go: that is the piece that clamps it to
 * the viewport and flips it below the chip when the chip is at the top of the
 * screen. Both were things `bottom-full` could not do.
 */
export function ReactionTooltipCard<E extends { emoji: string; url?: string | null }>({
  anchorRef,
  lines,
  overflow,
}: {
  /** The chip stack the card hangs off — what it is positioned against. */
  anchorRef: React.RefObject<HTMLElement | null>;
  lines: Array<{ entry: E; names: string[] }>;
  overflow: number;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<{
    key: string;
    width: number;
    height: number;
  } | null>(null);

  // Exactly what is drawn, so a measurement can say WHICH content it was of.
  const contentKey = `${overflow}|${lines
    .map((line) => `${line.entry.emoji}:${line.names.join(",")}`)
    .join("|")}`;

  /**
   * DERIVED, NOT STORED, so that it is already `null` on the render where the
   * content changes and the card goes back through the hidden measuring pass.
   * Read straight out of state it held the PREVIOUS content's box, and since
   * `pos` is computed from it in a passive effect, one frame was painted with
   * the new names at the old names' position — a reaction landing while someone
   * hovers is all it took. A frame with no card is invisible; a frame with the
   * card in the wrong place is not.
   *
   * Nulling it also un-caps the card for that pass, which is what keeps the
   * height below honest: measuring a capped card would feed the cap back in as
   * the desired height, and it could never grow again when the viewport did.
   */
  const size = measured && measured.key === contentKey ? measured : null;

  // MEASURED, NOT ESTIMATED. The card is as wide as the longest name list and
  // as tall as the number of distinct emoji, and nothing can keep a box on
  // screen without knowing how big it is. So the first pass renders it hidden
  // at the origin purely to be measured, and the second places it.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMeasured({ key: contentKey, width: rect.width, height: rect.height });
  }, [contentKey]);

  const pos = useAnchoredPosition(anchorRef, size !== null, {
    // Above the chip is where this card has always sat; the hook only flips it
    // when above cannot hold it and below has more room.
    preferred: "above",
    // A tooltip's height is not a guess, so `desiredHeight` is the measured
    // one: the default 96px floor is a list panel's and would reserve a box
    // three times this card's size, hanging it that far off the chip.
    desiredHeight: size?.height ?? 0,
    // The floor is the card's own height UNTIL that stops fitting. Pinning it
    // to the height outright — which is what this was — meant `maxHeight` could
    // never come back smaller than the card, so the cap below was a no-op and a
    // card taller than the room it had ran off the edge regardless.
    minHeight: Math.min(size?.height ?? 0, MIN_CARD_HEIGHT),
    width: size?.width,
    // The card's own cap, restated so the clamp and the card agree even if the
    // measurement is stale — a resize between the two is exactly when they
    // would otherwise diverge.
    maxWidth: CARD_MAX_WIDTH,
    align: "center",
    revision: size,
  });

  const placed = size !== null && pos !== null;
  /** The room ran out: the card is taller than the side it was given. */
  const scrollable = placed && size.height > pos.maxHeight;

  return (
    <div
      // The ref is on the INNER box, which is the one that carries the cap:
      // measuring the wrapper would measure the capped height back in.
      style={{
        position: "fixed",
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        maxWidth: pos?.width ?? cardMaxWidth(),
        // Hidden rather than unmounted for the measuring pass: a card that is
        // not laid out has no size to read.
        visibility: placed ? "visible" : "hidden",
      }}
      className="z-50 pointer-events-none"
    >
      <div
        ref={cardRef}
        className="bg-foreground text-background text-xs rounded-lg shadow-lg px-3 py-2 overflow-y-auto"
        /**
         * THE VERDICT ON HEIGHT IS USED, NOT JUST THE ONE ON WHICH SIDE.
         *
         * Flipping to the roomier side is half an answer: a card with more
         * distinct emoji than half the viewport holds is taller than EITHER
         * side, and was drawn straight past the edge with nothing to scroll.
         *
         * Set only when the cap actually binds. Below that it is the card's own
         * height and changes nothing, and `pointer-events` matters: the wrapper
         * is `pointer-events-none` because the card sits over the content above
         * it, so a card that fits must not start swallowing that content's
         * clicks to buy a scrollbar nobody needs.
         */
        style={scrollable ? { maxHeight: pos.maxHeight, pointerEvents: "auto" } : undefined}
      >
        {lines.map((line) => (
          <div key={line.entry.emoji} className="flex items-start gap-1.5">
            <Emoji emoji={line.entry.emoji} url={line.entry.url} size="sm" />
            <span className="min-w-0 break-words">{line.names.join(", ")}</span>
          </div>
        ))}
        {overflow > 0 && <div className="mt-1 text-background/70">og {overflow} andre</div>}
      </div>
    </div>
  );
}
